import { X, FileText } from 'lucide-react'

interface UploadThumbnailProps {
  src: string
  filename: string
  mimeType: string
  progress?: number
  onDelete?: () => void
  isDeleting?: boolean
}

/**
 * One uploaded file's thumbnail (image preview or document tile), with an
 * optional in-progress bar and an optional delete affordance. Shared by the
 * Upload Evidence page and the consumer claim evidence checklist (WPS-138 #6).
 */
export function UploadThumbnail({
  src,
  filename,
  mimeType,
  progress,
  onDelete,
  isDeleting,
}: UploadThumbnailProps) {
  const isImage = mimeType.startsWith('image/')
  const isUploading = progress !== undefined && progress < 100

  return (
    <div
      className={`group relative aspect-square overflow-hidden rounded-md border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] transition-all duration-200 ${
        isDeleting ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
      }`}
    >
      {isImage ? (
        <img src={src} alt={filename} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-2">
          <FileText size={20} className="text-[var(--ink-3)]" />
          <span className="line-clamp-2 text-center text-[10px] text-[var(--ink-3)]">
            {filename}
          </span>
        </div>
      )}

      {isUploading && (
        <div className="absolute inset-x-0 bottom-0 h-1 bg-[var(--rule-light)]">
          <div
            className="h-full bg-[var(--green)] transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {isUploading && <div className="absolute inset-0 bg-[var(--bg)]/40" />}

      {onDelete && !isUploading && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] opacity-0 transition-opacity duration-150 group-hover:opacity-100 active:opacity-100"
          aria-label={`Remove ${filename}`}
        >
          <X size={10} className="text-[var(--ink-2)]" />
        </button>
      )}
    </div>
  )
}
